import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from './../../app/services/auth.service';
import { ApiGatewayHelperService } from '../../app/services/apigateway-helper.service';
import { GroupHelperService } from './groups-helper.service';
import { ActivityViewHelperService } from './activity-view-helper.service';
import { Subject } from 'rxjs';

export interface FetchOptions {
    fetchGroupMembers?: boolean;
    limit?: number;
    polling?: boolean;
    refComment?: string;
    lastReadCommentTime?: any;
}

@Injectable()
export class GetStreamCommentsService {

    // User Information Cache
    private userInformationMap = {};
    // Map to store users for which information was not found
    private userInfoErrorMap = {};
    private currentUserData: any;
    constructor(
      private http: HttpClient,
      private authService: AuthService,
      private groupsHeleprService: GroupHelperService,
      private apiGatewayHelperService: ApiGatewayHelperService,
      private activityViewHelperService: ActivityViewHelperService,
    ) {
        const userData = this.authService.getUserData();

        this.currentUserData = {
            name: userData.firstName + ' ' + userData.lastName,
            role: userData.role,
            isSelf: true,
            userId: userData.extUserId,
        };
    }

    private prepareBaseUrl(groupId, itemCode) {
        return this.apiGatewayHelperService.getApiGatewayPrefixUrl()
              + `${this.activityViewHelperService.getSpace()}/comments/${groupId}/${encodeURIComponent(itemCode)}`;
    }

    private loadGroupMembersInCache(groupMembers) {
        groupMembers.forEach( (user: any) => {
            if (!this.userInformationMap[user.userid] && user.userid !== this.currentUserData.userId) {
                this.userInformationMap[user.userid] = {
                    name: user.first_name + ' ' + user.last_name,
                    role: user.enrollment.role,
                    userId: user.userid,
                };
                // Check the groupMembers data against userInfoErrorMap
                if (this.userInfoErrorMap[user.userid]) {
                    // Update the object using the reference from the userInfoErrorMap
                    Object.assign(this.userInfoErrorMap[user.userid], this.userInformationMap[user.userid]);
                    delete this.userInfoErrorMap[user.userid].error;
                    delete this.userInfoErrorMap[user.userid];
                }
            }
        });
    }

    async getComments(groupId, itemCode, lastReadCommentkey, options: FetchOptions = {}) {
        try {

            let url = this.prepareBaseUrl(groupId, itemCode) + '/get';

            // Load Group Members data in User Cache, if present
            if (this.groupsHeleprService.checkGroupMembers(groupId)) {
                this.loadGroupMembersInCache(this.groupsHeleprService.getGroupMembers(this.activityViewHelperService.getSpace(), groupId));
            }
            const excludeInfoForUserIds = Object.keys(this.userInformationMap);
            excludeInfoForUserIds.push(this.currentUserData.userId);

            const data = {
                limit: options.limit ? options.limit : 100,
                polling: options.polling,
                refComment: options.refComment,
                excludeInfoForUserIds,
                fetchInfoForUserIds: Object.keys(this.userInfoErrorMap),
            };

            if (options.fetchGroupMembers && !this.groupsHeleprService.checkGroupMembers(groupId)) {
                data['fetchGroupMembers'] = true;
            }

            const response = await this.http.post(url, data).toPromise() as any;
            if (response.usersData) {
                // Add usersData to existing cache, if any
                Object.assign(this.userInformationMap, response.usersData);
                // Check if received usersData against userInfoErrorMap
                Object.keys(response.usersData).forEach(userId => {
                    if (this.userInfoErrorMap[userId]) {
                        // Update the object using the reference from the userInfoErrorMap
                        Object.assign(this.userInfoErrorMap[userId], response.usersData[userId]);
                        delete this.userInfoErrorMap[userId].error;
                        delete this.userInfoErrorMap[userId];
                    }
                });
            }

            if (options.fetchGroupMembers) {
                if (!this.groupsHeleprService.checkGroupMembers(groupId)) {
                    this.groupsHeleprService.setGroupMembersForAGroup(groupId, response.groupMembers.entities);
                    this.loadGroupMembersInCache(response.groupMembers.entities);
                    response.groupMembers = response.groupMembers.entities;
                } else {
                    response.groupMembers = this.groupsHeleprService.getGroupMembers(this.activityViewHelperService.getSpace(), groupId);
                }
            }
            // Add user info from the cache
            response.commentsData.results = response.commentsData.results.map( comment => {
                if (comment.actor === this.currentUserData.userId) {
                    comment.actor = this.currentUserData;
                } else if (this.userInformationMap[comment.actor]) {
                    comment.actor = this.userInformationMap[comment.actor];
                } else {
                    if (!this.userInfoErrorMap[comment.actor]) {
                        // Create an object reference in commentsWithUserInfoError map
                        this.userInfoErrorMap[comment.actor] = {
                            error: true,
                            userId: comment.actor,
                        };
                    }
                    // Assign the actor from the reference to the comment, so that it can be updated later
                    comment.actor = this.userInfoErrorMap[comment.actor];
                }
                // Highlight all the comments after the last read comment
                if (!options.lastReadCommentTime || new Date(comment.time) > options.lastReadCommentTime) {
                    comment.highlightComment = !comment.actor.isSelf;
                }
                return comment;
            });
            // Store the last read comment time
            if (response.commentsData.results.length > 0 ) {
                const latestLastReadCommentTime = localStorage.getItem(lastReadCommentkey) ? new Date(parseInt(localStorage.getItem(lastReadCommentkey))) : '';
                if(!latestLastReadCommentTime || new Date(response.commentsData.results[0].time) > latestLastReadCommentTime) {
                    localStorage.setItem(lastReadCommentkey, new Date(response.commentsData.results[0].time).getTime().toString());
                }
            }

            return response;
        } catch(error) {
            if (error instanceof HttpErrorResponse) {
                error = { success: false, errorCode: 'OFFLINE_ERROR', statusCode: error.error.status };
            }
            return { success: false, error };
        }
    }

    addComment(groupId, itemCode, userText) {
        const subject = new Subject<object>();
        const classId = this.activityViewHelperService.getClass();
        const date = new Date().toISOString();

        // Create Local Copy of Comment
        const commentObject = {
            actor: this.currentUserData,
            class_id: classId,
            comment: userText,
            id: 'temp_' + Date.now(),
            group_id: groupId,
            object: `{ "item-code": "${itemCode}" }`,
            time: date.substring(0, date.length - 1), // Remove Z from the end
            verb: 'commented',
        };

        const url = this.prepareBaseUrl(groupId, itemCode) + '/add';

        this.http.post(url, { userText, classId }).toPromise().then( (res: any) => {
            if (res.success) {
                // Update Local Copy of Comment using Object Reference
                for (let key in res.commentData) {
                    commentObject[key] = res.commentData[key];
                }
                commentObject.actor = this.currentUserData;
                subject.next({ success: true, commentObject, serverTime: res.serverTime });
            } else {
                throw res;
            }
        }).catch(error => {
            if (error instanceof HttpErrorResponse) {
                error = { success: false, errorCode: 'OFFLINE_ERROR', statusCode: error.error.status };
            }
            subject.next({ success: false, commentObject, error });
        }) ;

        return { subject, commentObject};
    }
}
